import { memo } from 'react'
import { format } from 'date-fns'

import DeleteIcon from '@mui/icons-material/DeleteOutlined'
import { Grid, IconButton } from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { SimpleCollapsible } from '@/components/SimpleCollapsible'

import { SelectBox } from '../../SelectBox'
import { ConsoleTextField } from '../../StyledInputComponents'
import { BaseLicensingPeriod } from '../useLicensingPeriods'

export type StreamingLicensingPeriod = BaseLicensingPeriod & {
  maxEvents?: number
  purchasedEvents?: number
}

type Props = {
  value?: StreamingLicensingPeriod
  onDelete: () => void
  onChange: (period: Partial<StreamingLicensingPeriod>) => void
  expanded?: boolean
  usedYears: Set<string>
  onExpandedChange?: (expanded: boolean) => void
  hasError?: string | boolean
  autoBooking?: boolean
}

export const MIN_YEAR = 2010
export const MAX_YEAR = 2050

const LICENSING_PERIOD_YEARS = Array.from(
  { length: MAX_YEAR - MIN_YEAR + 1 },
  (_, i) => MIN_YEAR + i
).map((year) => year.toString())

const DGELicensingPeriodComponent = ({
  value,
  onDelete,
  onChange,
  expanded,
  usedYears = new Set(),
  onExpandedChange,
  hasError = false,
  autoBooking = false
}: Props) => {
  const description = value?.year
    ? [
        value?.startDate &&
          value?.endDate &&
          `${format(new Date(value.startDate.split('T')[0]), 'MMM dd')} - ${format(new Date(value.endDate.split('T')[0]), 'MMM dd')}`,
        autoBooking
          ? 'Unlimited events'
          : (value?.maxEvents !== undefined || value?.purchasedEvents !== undefined) &&
            `${value?.maxEvents || 0} max events ⋅ ${value?.purchasedEvents || 0} purchased`
      ]
        .filter(Boolean)
        .join(' ⋅ ')
    : ''

  return (
    <SimpleCollapsible
      title={value?.year ? `${value.year}` : 'New Period'}
      expanded={expanded}
      onChange={onExpandedChange}
      description={description}
      error={hasError}
      actions={
        <IconButton onClick={onDelete} size="small">
          <DeleteIcon />
        </IconButton>
      }
    >
      <Grid container rowSpacing={1} columnSpacing={2} pb={0.25}>
        <Grid size={{ xs: 3 }}>
          <SelectBox
            options={LICENSING_PERIOD_YEARS.filter((year) => !usedYears.has(year))}
            value={value?.year || ''}
            onChange={(year) =>
              onChange({
                year,
                startDate: `${year}-01-01`,
                endDate: `${year}-12-31`
              })
            }
            label="Year"
            fullWidth
            required
          />
        </Grid>

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid size={{ xs: 4.5 }}>
            <DatePicker
              label="Start Date"
              value={value?.startDate ? new Date(value.startDate.split('T')[0]) : null}
              format="dd-MM-yyyy"
              onChange={(date) => {
                if (!date) {
                  onChange({ startDate: '' })
                  return
                }

                const year = date.getFullYear()
                const month = String(date.getMonth() + 1).padStart(2, '0')
                const day = String(date.getDate()).padStart(2, '0')

                onChange({
                  startDate: `${year}-${month}-${day}`,
                  endDate: ''
                })
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  required: true
                }
              }}
              minDate={value?.year ? new Date(`${value.year}-01-01`) : undefined}
              maxDate={value?.year ? new Date(`${value.year}-12-31`) : undefined}
              views={['day']}
              shouldDisableYear={(date) => date.getFullYear().toString() !== value?.year}
              disabled={!value?.year}
            />
          </Grid>
          <Grid size={{ xs: 4.5 }}>
            <DatePicker
              label="End Date"
              value={value?.endDate ? new Date(value.endDate.split('T')[0]) : null}
              format="dd-MM-yyyy"
              onChange={(date) => {
                if (!date) {
                  onChange({ endDate: '' })
                  return
                }

                const year = date.getFullYear()
                const month = String(date.getMonth() + 1).padStart(2, '0')
                const day = String(date.getDate()).padStart(2, '0')

                onChange({
                  endDate: `${year}-${month}-${day}`
                })
              }}
              minDate={value?.startDate ? new Date(value.startDate) : undefined}
              maxDate={value?.year ? new Date(`${value.year}-12-31`) : undefined}
              views={['day']}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  required: true
                }
              }}
              shouldDisableYear={(date) => date.getFullYear().toString() !== value?.year}
              disabled={!value?.year || !value?.startDate}
            />
          </Grid>
        </LocalizationProvider>

        <Grid size={{ xs: 6 }}>
          <ConsoleTextField
            label="Max Events"
            type={autoBooking ? 'text' : 'number'}
            value={autoBooking ? 'Unlimited' : (value?.maxEvents ?? 0)}
            disabled={autoBooking || !value?.year}
            onChange={(e) =>
              onChange({
                maxEvents: parseInt(e.target.value) || 0
              })
            }
          />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <ConsoleTextField
            label="Purchased Events"
            type={autoBooking ? 'text' : 'number'}
            value={autoBooking ? 'Unlimited' : (value?.purchasedEvents ?? 0)}
            disabled={autoBooking || !value?.year}
            onChange={(e) =>
              onChange({
                purchasedEvents: parseInt(e.target.value) || 0
              })
            }
          />
        </Grid>
      </Grid>
    </SimpleCollapsible>
  )
}

export const DGELicensingPeriod = memo(DGELicensingPeriodComponent)
